import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  RadioGroup as MuiRadioGroup,
  Radio,
  SxProps,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
  ALGOLIA_ASSESSMENT_ICD_CODES,
  FUNCTION_DIAGNOSIS_PRIORITIES,
  MUSCOSKELETAL_RESILIENCE,
  OPERATION_INDEXES,
  PREDICAMENT_STATE_YES,
  SCHEMA_VERSION,
  TYPE_OF_REHAB,
} from "core/consts";
import { isFilled } from "core/schemas/utils";
import { GKVMedicalFormData, SchemaVersion } from "core/types";
import ConnectedCheckboxGroup from "ds_legacy/components/CheckboxGroup";
import CheckboxGroupV2 from "ds_legacy/components/CheckboxGroupV2";
import RadioGroupV2 from "ds_legacy/components/RadioGroupV2";
import {
  CUSTOM_BLACK,
  RADIO_WHITE_BACKGROUND,
} from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import {
  important,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_18,
  FONT_WEIGHT_BOLD,
  Subheading,
} from "ds_legacy/materials/typography";
import AlgoliaSelect from "dsl/atoms/AlgoliaSelect";
import {
  DatePickerWithLabel,
  Footnote,
  FootnoteDescription,
  FootnoteLabel,
  FormSectionsWrapper,
  GAP,
  getDefaultVisibleOperationRows,
  LARGE_INPUT_WIDTH,
  OperationsSubForm,
  PatientFormsSectionHeader,
  PatientFormSubheading,
  patientSubheadingSx,
  Section,
  SectionRow,
  SelectWithLabel,
  SMALL_INPUT_MIN_WIDTH,
  TextAreaWithLabel,
  TextInputWithLabel,
  YesNoRadioWithLabel,
} from "dsl/ecosystems/PatientForms/components";
import { SingerAssessmentContent } from "dsl/organisms/IndependenceScale/SingerAssessment";
import { useRef } from "react";
import {
  FormElementRenderProps,
  FormWatcher,
  isValid,
} from "react-forms-state/src";
import { ObservableValue } from "react-forms-state/src/Context";
import { Observable } from "rxjs";
import { useTranslations } from "translations";
import Translations from "translations/types";
import {
  SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS,
  useComplicationOptions,
  useDoctorAuthorizationOptions,
  useEarlyRehabCriteriaOptions,
  useGenderOptions,
  useICDReasonOptions,
  useInfectionOptions,
  useMusculoskeletalResilienceOptions,
  useRehabAdmissionOptions,
  useRehabFocusOptions,
  useRehabTypeDirectiveOptions,
  useRehabTypeOptions,
  useSpecialRehabRequirementOptions,
  useTransportOptions,
} from "./hooks";

const FOOTNOTE: Footnote = {
  description: (translations: Translations) =>
    translations.patientForms.medicalForm.sectionTwo.footnoteOne,
  label: (translations: Translations) =>
    translations.patientForms.medicalForm.sectionTwo.icdCode,
  superText: 1,
};

type IcdHit = {
  code: string;
  description: string;
};

const renderIcdHit = ({ code, description }: IcdHit, index: number) => {
  return {
    description,
    id: index,
    label: `(${code}) ${description}`,
    value: code,
  };
};

export function GKVMedicalFormPresenter({
  onChange,
  schemaVersion,
  shouldSkipRequiredValidation,
  valueChangeObs,
}: {
  onChange: (value: any, statePath: string) => void;
  schemaVersion?: SchemaVersion;
  shouldSkipRequiredValidation: boolean;
  valueChangeObs: Observable<ObservableValue> | undefined;
}) {
  const translations = useTranslations();
  const formStateRef = useRef<Partial<GKVMedicalFormData> | null>(null);
  const icdReasonOptions = useICDReasonOptions();
  const genderOptions = useGenderOptions();
  const rehabAdmissionOptions = useRehabAdmissionOptions();
  const infectionOptions = useInfectionOptions(shouldSkipRequiredValidation);
  const complicationOptions = useComplicationOptions();
  const musculoskeletalResilienceOptions =
    useMusculoskeletalResilienceOptions();
  const rehabFocusOptions = useRehabFocusOptions();
  const rehabTypeOptions = useRehabTypeOptions();
  const earlyRehabCriteriaOptions = useEarlyRehabCriteriaOptions();
  const rehabTypeDirectiveOptions = useRehabTypeDirectiveOptions();
  const specialRehabRequirementOptions = useSpecialRehabRequirementOptions();
  const transportOptions = useTransportOptions();
  const doctorAuthorizationOptions = useDoctorAuthorizationOptions();

  return (
    <VerticalLayout width="100%">
      <FormElementRenderProps elementName="hospital_ik_number">
        {({ value }) => (
          <input name="hospital_ik_number" type="hidden" value={value ?? ""} />
        )}
      </FormElementRenderProps>
      <FormElementRenderProps elementName="insurance_ik_number">
        {({ value }) => (
          <input name="insurance_ik_number" type="hidden" value={value ?? ""} />
        )}
      </FormElementRenderProps>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.medicalForm.sectionOne.title}
        </PatientFormsSectionHeader>
        <Section>
          <Subheading
            as="h5"
            margin={margin(0, 0, 0.5, 0.5)}
            style={{
              fontSize: FONT_SIZE_18,
              fontWeight: FONT_WEIGHT_BOLD,
            }}
          >
            {
              translations.patientForms.medicalForm.sectionOne
                .informationInsuredPerson
            }
          </Subheading>
          <RadioGroupV2
            elementName="gender"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={{
              color: important(CUSTOM_BLACK),
              fontSize: FONT_SIZE_14,
              fontWeight: FONT_WEIGHT_BOLD,
            }}
            isRequired={!shouldSkipRequiredValidation}
            label={translations.patientForms.drvGeneralForm.sectionTwo.gender}
            options={genderOptions}
            radioGroupSx={{ flexDirection: "row", paddingTop: sizing(1) }}
            radioSx={RADIO_WHITE_BACKGROUND}
          />
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              elementName="patient_first_name"
              isBold
              isLarge
              label={translations.patientForms.medicalForm.sectionOne.firstName}
              required={!shouldSkipRequiredValidation}
            />
            <TextInputWithLabel
              elementName="patient_last_name"
              isBold
              isLarge
              label={translations.patientForms.medicalForm.sectionOne.lastName}
              required={!shouldSkipRequiredValidation}
            />
          </SectionRow>
          <SectionRow>
            <DatePickerWithLabel
              elementName="patient_birthdate"
              inputSx={{
                minWidth: SMALL_INPUT_MIN_WIDTH,
                width: "100%",
              }}
              isBold
              isRequired={!shouldSkipRequiredValidation}
              label={
                translations.patientForms.generalForm.insuredPerson.dateOfBirth
              }
              wrapperSx={{ width: LARGE_INPUT_WIDTH }}
            />
            <TextInputWithLabel
              elementName="insurance_number"
              isBold
              isLarge
              label={
                translations.patientForms.generalForm.insuredPerson
                  .insuranceNumber
              }
              required={!shouldSkipRequiredValidation}
            />
          </SectionRow>
          <PatientFormSubheading>
            {
              translations.patientForms.medicalForm.sectionOne
                .hospitalStayInformation
            }
          </PatientFormSubheading>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <DatePickerWithLabel
              elementName="admission_date"
              inputSx={{
                minWidth: SMALL_INPUT_MIN_WIDTH,
                width: "100%",
              }}
              isBold
              isRequired={!shouldSkipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionOne.intakeDate
              }
              wrapperSx={{ width: LARGE_INPUT_WIDTH }}
            />
            <DatePickerWithLabel
              elementName="discharge_date"
              inputSx={{
                minWidth: SMALL_INPUT_MIN_WIDTH,
                width: "100%",
              }}
              isBold
              isRequired={!shouldSkipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionOne.dischargeDate
              }
              wrapperSx={{ width: LARGE_INPUT_WIDTH }}
            />
          </SectionRow>
          <YesNoRadioWithLabel
            elementName="early_rehabilitation_measures"
            formLabelSx={patientSubheadingSx}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionOne
                .earlyRehabMeasures
            }
            shouldStartWithNo
          />
          <PatientFormSubheading withBottomPadding>
            {translations.patientForms.medicalForm.sectionOne.admissionRehab +
              (!shouldSkipRequiredValidation ? " *" : "")}
          </PatientFormSubheading>
          <RadioGroupV2
            elementName="rehab_admission_type"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={visuallyHidden as SxProps}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .accessibilityGuardianType
            }
            options={rehabAdmissionOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            sideMutation={(_value, mutation) => {
              mutation(null, "rehab_admission_outside_two_weeks_explanation");
              mutation(null, "rehab_admission_outside_two_weeks_start_date");
              mutation(null, "rehab_admission_within_two_weeks_start_date");
            }}
          />
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.medicalForm.sectionTwo.title}
        </PatientFormsSectionHeader>
        <Section>
          <Subheading
            as="h5"
            margin={margin(0, 0, 0.5, 0.5)}
            style={{
              fontSize: FONT_SIZE_18,
              fontWeight: FONT_WEIGHT_BOLD,
            }}
          >
            {translations.patientForms.medicalForm.sectionTwo.functionDiagnoses}
          </Subheading>
          {Object.values(FUNCTION_DIAGNOSIS_PRIORITIES).map((priority) => (
            <SectionRow
              key={priority}
              customPadding={padding(2, 0, 0)}
              style={{ alignItems: "center", flexWrap: "nowrap" }}
            >
              <Body
                as="p"
                margin={margin(0, 0, 1)}
                style={{ alignSelf: "center" }}
              >
                {priority}
              </Body>
              <TextInputWithLabel
                elementName={`icd_code_${priority}_diagnosis`}
                isBold
                label={
                  translations.patientForms.medicalForm.sectionTwo
                    .diagnosisDescription
                }
                labelWrapperSx={{
                  minWidth: undefined,
                  width: "40%",
                }}
                required={priority === 1 && !shouldSkipRequiredValidation}
              />
              <div className="w-[20%]">
                <h3 className="mb-6 text-sm font-bold">
                  <FootnoteLabel footnote={FOOTNOTE} />
                </h3>
                <AlgoliaSelect
                  algoliaAnalyticsName={ALGOLIA_ASSESSMENT_ICD_CODES}
                  elementName={`icd_code_${priority}_code`}
                  indexName="icd_codes"
                  isConnected
                  isRequired={priority === 1 && !shouldSkipRequiredValidation}
                  maxOptions={5}
                  placeholder={
                    translations.general.selectDefaultPlaceholderShort
                  }
                  renderHits={renderIcdHit}
                  shouldSearchWithoutTyping
                  shouldTruncateMenuItems
                  withShortValue
                />
              </div>
              <SelectWithLabel
                elementName={`icd_code_${priority}_reason`}
                inputSx={{
                  fontSize: FONT_SIZE_14,
                  minWidth: undefined,
                  width: "100%",
                }}
                isBold
                label={translations.patientForms.medicalForm.sectionTwo.cause}
                nullable={priority === 1 ? false : true}
                options={icdReasonOptions}
                required={priority === 1 && !shouldSkipRequiredValidation}
                wrapperSx={{ width: "40%" }}
              />
            </SectionRow>
          ))}
          <VerticalLayout
            style={{
              boxSizing: "border-box",
              padding: padding(GAP, 0, 0),
            }}
          >
            <FootnoteDescription footnote={FOOTNOTE} />
          </VerticalLayout>
          <YesNoRadioWithLabel
            elementName="surgery_performed"
            formLabelSx={patientSubheadingSx}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionTwo
                .performedTreatments
            }
            shouldStartWithNo
            sideMutation={(_value, mutation) => {
              Object.values(OPERATION_INDEXES).forEach((opsIndex) => {
                const prefix = `operation_${opsIndex}`;
                mutation(null, `${prefix}_date`);
                mutation(null, `${prefix}_wound_closed`);
                mutation(null, `${prefix}_ops`);
              });
            }}
            subForms={{
              [PREDICAMENT_STATE_YES]: (
                <FormWatcher watchPath="">
                  {({ value }: { value: Partial<GKVMedicalFormData> }) => {
                    return (
                      <OperationsSubForm
                        initialVisibleOpsFields={getDefaultVisibleOperationRows(
                          value,
                        )}
                      />
                    );
                  }}
                </FormWatcher>
              ),
            }}
          />
          <div className="pt-4">
            <TextAreaWithLabel
              elementName="other_treatments"
              isBold
              label={
                translations.patientForms.medicalForm.sectionTwo.otherTreatments
              }
              sx={{ margin: margin(0) }}
              withFlatModel
            />
          </div>
          <YesNoRadioWithLabel
            elementName="complication_in_treatment_process"
            formLabelSx={patientSubheadingSx}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionTwo.complications
            }
            shouldStartWithNo
            sideMutation={(_value, mutate) => {
              complicationOptions.forEach((option) => {
                mutate(null, option.elementName);
              });
            }}
            subForms={{
              [PREDICAMENT_STATE_YES]: (
                <VerticalLayout margin={margin(0, 0, 0, 4)} overflow="visible">
                  <FormControl component="fieldset" sx={{ gap: sizing(1) }}>
                    <Body as="legend" style={visuallyHidden}>
                      {
                        translations.patientForms.medicalForm.sectionTwo
                          .complications
                      }
                    </Body>
                    <FormWatcher watchPath="">
                      {({ value: formData }) => {
                        formStateRef.current = formData;

                        const hideError = Boolean(
                          formData?.internal_complications_hide_error,
                        );

                        const handleComplicationChange =
                          (fieldName: string) =>
                          (
                            _value: any,
                            mutate: (v: any, n: string) => void,
                          ) => {
                            const current = formStateRef.current || {};
                            const nextState = {
                              ...current,
                              [fieldName]: _value,
                            } as Partial<GKVMedicalFormData>;

                            // Clear other fields only if they are empty (single mutate)
                            complicationOptions.forEach((opt) => {
                              if (opt.elementName === fieldName) return;
                              const otherValue =
                                nextState[
                                  opt.elementName as keyof GKVMedicalFormData
                                ];
                              if (!isFilled(otherValue)) {
                                mutate("", opt.elementName);
                              }
                            });

                            // Derive validation: hide error if any of the three fields is filled
                            const anyFilled =
                              isFilled(
                                nextState.complication_cause_maindiagnosis_description,
                              ) ||
                              isFilled(
                                nextState.complication_cause_cardiovascular_description,
                              ) ||
                              isFilled(
                                nextState.complication_cause_other_description,
                              );

                            if (
                              anyFilled !==
                              current?.internal_complications_hide_error
                            ) {
                              mutate(
                                anyFilled,
                                "internal_complications_hide_error",
                              );
                            }
                          };

                        const mainDiagnosisValue =
                          formData?.complication_cause_maindiagnosis_description;
                        const cardiovascularValue =
                          formData?.complication_cause_cardiovascular_description;
                        const otherValue =
                          formData?.complication_cause_other_description;

                        const allEmpty =
                          !isFilled(mainDiagnosisValue) &&
                          !isFilled(cardiovascularValue) &&
                          !isFilled(otherValue);
                        return (
                          <>
                            {complicationOptions.map(
                              ({ elementName, label }) => {
                                const isThisFieldFilled = isFilled(
                                  formData?.[
                                    elementName as keyof GKVMedicalFormData
                                  ],
                                );
                                const isRequired =
                                  !shouldSkipRequiredValidation &&
                                  (allEmpty || isThisFieldFilled);
                                return (
                                  <TextAreaWithLabel
                                    key={elementName}
                                    elementName={elementName}
                                    hideError={hideError}
                                    label={label}
                                    required={isRequired}
                                    sideMutation={handleComplicationChange(
                                      elementName,
                                    )}
                                    sx={{ margin: margin(0) }}
                                    withFlatModel
                                  />
                                );
                              },
                            )}
                          </>
                        );
                      }}
                    </FormWatcher>
                  </FormControl>
                </VerticalLayout>
              ),
            }}
          />
          <RadioGroupV2
            elementName="infections_state"
            formControlLabelSx={{
              marginLeft: 0,
              paddingBottom: sizing(0.5),
            }}
            formLabelSx={{
              color: important(CUSTOM_BLACK),
              fontSize: FONT_SIZE_14,
              fontWeight: FONT_WEIGHT_BOLD,
              padding: padding(1, 0),
            }}
            isRequired={!shouldSkipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionTwo.germs}
            options={infectionOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            sideMutation={(_value, mutation) => {
              mutation(null, "infections");
            }}
            withFlatModel
          />

          <PatientFormSubheading withBottomPadding>
            {
              translations.patientForms.medicalForm.sectionTwo
                .longTermImpairments
            }
          </PatientFormSubheading>
          <VerticalLayout>
            <SingerAssessmentContent
              isGKVForm
              shouldSkipRequiredValidation={shouldSkipRequiredValidation}
              valueChangeObs={valueChangeObs}
              onChange={onChange}
            />
          </VerticalLayout>
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.medicalForm.sectionThree.title}
        </PatientFormsSectionHeader>
        <Section>
          <Subheading
            as="h5"
            margin={margin(0, 0, 0.5, 0.5)}
            style={{
              fontSize: FONT_SIZE_18,
              fontWeight: FONT_WEIGHT_BOLD,
              padding: padding(0, 0, 1),
            }}
          >
            {translations.patientForms.medicalForm.sectionThree.resilience}
            {!shouldSkipRequiredValidation ? " *" : ""}
          </Subheading>
          <YesNoRadioWithLabel
            elementName="limited_capacity"
            formLabelSx={visuallyHidden}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionThree.resilience
            }
            shouldStartWithNo
          />
          <PatientFormSubheading withBottomPadding>
            {
              translations.patientForms.medicalForm.sectionThree
                .musculoskeletalSystem
            }
          </PatientFormSubheading>
          <RadioGroupV2
            elementName="reha_capacity"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={visuallyHidden as SxProps}
            label={
              translations.patientForms.medicalForm.sectionThree
                .musculoskeletalSystem
            }
            options={musculoskeletalResilienceOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            sideMutation={(value, mutate) => {
              if (value !== MUSCOSKELETAL_RESILIENCE.PARTIALLY) {
                return mutate(null, "reha_capacity_partial");
              }
            }}
          />
          <YesNoRadioWithLabel
            elementName="interim_prosthesis_fitted"
            formLabelSx={{
              color: important(CUSTOM_BLACK),
              fontSize: FONT_SIZE_14,
              fontWeight: FONT_WEIGHT_BOLD,
              padding: padding(GAP, 0, 0.5),
            }}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionThree.prosthesis
            }
            shouldStartWithNo
          />
          <TextAreaWithLabel
            elementName="reha_capacity_other"
            isBold
            label={translations.patientForms.medicalForm.sectionThree.specifics}
            sx={{ margin: margin(0) }}
          />
          <PatientFormSubheading withBottomPadding>
            {translations.patientForms.medicalForm.sectionThree.earlyRehabIndex}
          </PatientFormSubheading>
          <ConnectedCheckboxGroup
            elementName="reha_capacity_early_rehab_criteria"
            formGroupStyle={{ gap: sizing(1) }}
            items={earlyRehabCriteriaOptions}
            label={
              translations.patientForms.medicalForm.sectionThree.earlyRehabIndex
            }
            labelStyle={visuallyHidden}
          />
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.medicalForm.sectionFour.title}
        </PatientFormsSectionHeader>
        <Section>
          <Subheading
            as="h5"
            margin={margin(0, 0, 0.5, 0.5)}
            style={{
              fontSize: FONT_SIZE_16,
              fontWeight: FONT_WEIGHT_BOLD,
            }}
          >
            {translations.patientForms.medicalForm.sectionFour.rehabGoals}
            {!shouldSkipRequiredValidation && " *"}
          </Subheading>
          <TextAreaWithLabel
            elementName="rehabilitation_goals"
            formLabelSx={visuallyHidden as SxProps}
            label={translations.patientForms.medicalForm.sectionFour.rehabGoals}
            required={!shouldSkipRequiredValidation}
          />
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.medicalForm.sectionFive.title}
        </PatientFormsSectionHeader>
        <Section>
          <Subheading
            as="h5"
            margin={margin(0, 0, 0.5, 0.5)}
            style={{
              fontSize: FONT_SIZE_18,
              fontWeight: FONT_WEIGHT_BOLD,
              padding: padding(0, 0, 1),
            }}
          >
            {translations.patientForms.medicalForm.sectionFive.rehabFocus}
            {!shouldSkipRequiredValidation ? " *" : ""}
          </Subheading>
          <RadioGroupV2
            elementName="rehab_focus"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={visuallyHidden as SxProps}
            isRequired={!shouldSkipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionFive.rehabFocus}
            options={rehabFocusOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            sideMutation={(_value, mutation) => {
              mutation(null, "rehab_focus_other_description");
            }}
          />
          <RadioGroupV2
            elementName="recommended_reha_type"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={patientSubheadingSx as SxProps}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionFive.typeOfRehab
            }
            options={rehabTypeOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
          />
          <FormWatcher watchPath="recommended_reha_type">
            {({ watchedValue: recommended_reha_type }) => {
              const isRequired = (
                [
                  TYPE_OF_REHAB.AMBULANT_MOBIL,
                  TYPE_OF_REHAB.STATIONARY,
                ] as number[]
              ).includes(recommended_reha_type);

              return (
                <TextAreaWithLabel
                  elementName="recommended_reha_type_reason"
                  label={
                    translations.patientForms.medicalForm.sectionTwo
                      .complicationsDescriptionLabel
                  }
                  required={isRequired && !shouldSkipRequiredValidation}
                  withFlatModel
                />
              );
            }}
          </FormWatcher>
          <YesNoRadioWithLabel
            elementName="rehab_directive_state"
            formLabelSx={patientSubheadingSx}
            isRequired={!shouldSkipRequiredValidation}
            label={translations.patientForms.medicalForm.sectionFive.rehabRl}
            shouldStartWithNo
            sideMutation={(_value, mutate) => {
              mutate(null, "rehab_directive_type");
            }}
            subForms={{
              [PREDICAMENT_STATE_YES]:
                schemaVersion === SCHEMA_VERSION.V2_0_0 ? (
                  <FormElementRenderProps
                    elementName="rehab_directive_type"
                    withFlatModel
                  >
                    {({ onChange: onFieldChange, validation, value }) => {
                      const currentValue: string | undefined = Array.isArray(
                        value,
                      )
                        ? value[0]
                        : value;
                      const hasError = !isValid(validation);

                      return (
                        <FormControl
                          component="fieldset"
                          error={hasError}
                          variant="standard"
                        >
                          <FormLabel
                            component="legend"
                            sx={visuallyHidden as SxProps}
                          >
                            {
                              translations.patientForms.medicalForm.sectionFive
                                .rehabDirectiveTypeLabel
                            }
                          </FormLabel>
                          <MuiRadioGroup
                            value={currentValue ?? ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              onFieldChange(newValue ? [newValue] : null);
                            }}
                          >
                            {rehabTypeDirectiveOptions.map((option) => (
                              <FormControlLabel
                                key={option.value}
                                control={<Radio color="primary" size="small" />}
                                data-testid={`radio_rehab_directive_type_${option.value}`}
                                label={option.label}
                                sx={{
                                  margin: margin(0, 0, 0, 4),
                                  paddingBottom: sizing(0.5),
                                }}
                                value={option.value}
                              />
                            ))}
                          </MuiRadioGroup>
                          {hasError && (
                            <FormHelperText>
                              {
                                translations.patientForms.generalForm
                                  .fieldRequired
                              }
                            </FormHelperText>
                          )}
                        </FormControl>
                      );
                    }}
                  </FormElementRenderProps>
                ) : (
                  <CheckboxGroupV2
                    elementName="rehab_directive_type"
                    formControlLabelSx={{
                      margin: margin(0, 0, 0, 4),
                      paddingBottom: sizing(0.5),
                    }}
                    formLabelSx={visuallyHidden}
                    label={
                      translations.patientForms.medicalForm.sectionFive
                        .rehabDirectiveTypeLabel
                    }
                    options={rehabTypeDirectiveOptions}
                    withFlatModel
                  />
                ),
            }}
          />
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.medicalForm.sectionSix.title}
        </PatientFormsSectionHeader>
        <Section>
          <YesNoRadioWithLabel
            elementName="special_requirements_required"
            formLabelSx={patientSubheadingSx}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionSix
                .specialRequirements
            }
            shouldStartWithNo
            sideMutation={(_, mutate) => {
              mutate(null, "special_requirements");
              Object.values(SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS).forEach(
                (elementName) => {
                  mutate(null, elementName);
                },
              );
            }}
            subForms={{
              [PREDICAMENT_STATE_YES]: (
                <CheckboxGroupV2
                  elementName="special_requirements"
                  formControlSx={{
                    margin: margin(0, 0, 0, 4),
                    padding: padding(0, 0, 1),
                  }}
                  formLabelSx={visuallyHidden}
                  isRequired
                  label={
                    translations.patientForms.medicalForm.sectionSix
                      .specialRequirementsSelectOptionsLabel
                  }
                  options={specialRehabRequirementOptions}
                  sideMutation={(specialRequirementsValue, mutate) => {
                    Object.entries(
                      SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS,
                    ).forEach(([value, elementName]) => {
                      if (!specialRequirementsValue.includes(value)) {
                        mutate(null, elementName);
                      }
                    });
                  }}
                  withFlatModel
                />
              ),
            }}
          />
          <RadioGroupV2
            elementName="transport_type"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={patientSubheadingSx as SxProps}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionSix.transportation
            }
            options={transportOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            sideMutation={(_value, mutation) => {
              mutation(null, "transport_type_taxi_specification");
              mutation(null, "transport_type_ambulance_specification");
              mutation(null, "transport_type_ambulance_description");
            }}
          />
          <YesNoRadioWithLabel
            elementName="require_accompanying_person"
            formLabelSx={{
              color: important(CUSTOM_BLACK),
              fontSize: FONT_SIZE_14,
              fontWeight: FONT_WEIGHT_BOLD,
              padding: padding(GAP, 0, 0.5),
            }}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionSix
                .accompanyingPerson
            }
            shouldStartWithNo
          />
          <PatientFormSubheading>
            {
              translations.patientForms.medicalForm.sectionSix
                .contactInformationDoctor
            }
          </PatientFormSubheading>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              elementName="doctor_in_charge_in_hospital_first_name"
              isBold
              isLarge
              label={translations.people.firstName}
              required={!shouldSkipRequiredValidation}
            />
            <TextInputWithLabel
              elementName="doctor_in_charge_in_hospital_last_name"
              isBold
              isLarge
              label={translations.people.lastName}
              required={!shouldSkipRequiredValidation}
            />
          </SectionRow>
          <SectionRow customPadding={padding(2, 0, 0)}>
            <TextInputWithLabel
              elementName="doctor_in_charge_in_hospital_phone"
              isBold
              isLarge
              label={
                translations.patientForms.medicalForm.sectionSix.phoneDoctor
              }
              required={!shouldSkipRequiredValidation}
            />
            <DatePickerWithLabel
              elementName="doctor_exam_date"
              inputSx={{
                minWidth: SMALL_INPUT_MIN_WIDTH,
                width: "100%",
              }}
              isBold
              isRequired={!shouldSkipRequiredValidation}
              label={
                translations.patientForms.medicalForm.sectionSix
                  .doctorExamDateLabel
              }
              wrapperSx={{ width: LARGE_INPUT_WIDTH }}
            />
          </SectionRow>
        </Section>
        {!shouldSkipRequiredValidation && (
          <RadioGroupV2
            elementName="approved_by"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={patientSubheadingSx as SxProps}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.insuranceApp.eligibilityTab.approvalFlow
                .authorisationQuestionTitle
            }
            options={doctorAuthorizationOptions}
            radioGroupSx={{
              gap: "16px",
            }}
            radioSx={RADIO_WHITE_BACKGROUND}
          />
        )}
      </FormSectionsWrapper>
    </VerticalLayout>
  );
}
